import {
  CircularProgress,
  Grid,
  Theme,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import {
  DefaultButton,
  DefaultCard,
  DisplayItem,
  SearchableDropDown,
  TextField,
  colors,
  useFormHook,
} from 'design-system';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';

// Data Layer
import { getLoggedRole } from 'data-layer-cr/dist/authentication';
import { isSpecialPlan } from 'data-layer-cr/dist/functions/quotations';
import { GeneralCatalogItem } from 'data-layer-cr/dist/models';
import QuotationProfile from 'data-layer-cr/dist/models/QuotationProfile';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { getDynamicRenderConfigs } from 'data-layer-cr/dist/slices/dynamicRender/actions/dynamicRenderActions';
import { CONFIG_REQUEST } from 'data-layer-cr/dist/slices/dynamicRender/requestIndicator';

import NetworkClient from 'src/services/NetworkClient';

// Redux
import FormValueTypes from 'src/models/helperTypes/FormValueTypes';
import PricingProfileResponse from 'src/models/responseTypes/PricingProfileResponse';
import { getAdditionalBrokers } from 'src/store/catalogs/actions';
import { REQUEST_ADDITIONAL_BROKERS } from 'src/store/catalogs/requestIdentifiers';
import { getAutoPxResult } from 'src/store/profile/actions';
import { GET_AUTO_PX } from 'src/store/profile/requestIdentifiers';
import {
  getSubagents,
  updateLicenseFormValues,
} from 'src/store/quotation/actions';
import { RootState } from 'src/store/rootReducer';

// Analytics
import { COLLECTIVE_SECTION_STEP, trackEvent } from 'src/analytics';

// Methods
import SubAgentItem from 'src/models/Quotation/SubAgentItem';
import makeLicenseFormValidation from './makeLicenseFormValidation';

import makeFormStyles from '../styles/makeFormStyles';
import { AdditionalBrokers, BrokerPercentageHelp } from './components';
import {
  additionalBrokerHasError,
  brokerReducer,
  convertToAdditionalBrokerForm,
  matchesCobrokerageLicense,
} from './customFormHelpers';

export type LicenseFormData = {
  license: string;
  profile: number;
  subagent: string;
  additionalError: boolean;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    svgArt: {
      display: 'none',
      [theme.breakpoints.down('xs')]: {
        display: 'block',
        marginTop: -30,
        marginBottom: -30,
      },
    },
    content: {
      margin: theme.spacing(1),
      marginTop: theme.spacing(5),
      [theme.breakpoints.down('sm')]: {
        marginTop: theme.spacing(1),
      },
    },
    inputStyles: {
      '& .Mui-disabled': {
        color: colors.madison,
      },
    },
    brokeragePercent: {
      '& .MuiInput-root': {
        height: 20,
      },
    },
  }),
);

function customTypographyText() {
  return (
    <Typography
      variant="caption"
      style={{
        color: colors.bright_pink,
        lineHeight: '18px',
      }}
    >
      Nota: Recuerda por ser Colaborador y cotizar bajo una licencia de
      Corredor, el cliente NO podrá recibir el producto
      <span
        style={{
          fontWeight: 'bold',
          color: colors.blue_zodiac,
        }}
      >
        {' '}
        Auto
      </span>
      <span
        style={{
          fontWeight: 'bold',
          color: colors.bright_turquoise,
        }}
      >
        PX
      </span>
      .
    </Typography>
  );
}

function adittionalBrokers(hasAdditionalBrokers: boolean, brokers: number) {
  if (hasAdditionalBrokers) {
    return (
      <Grid
        item
        xs={12}
        container
        alignItems="center"
        justifyContent="space-between"
        wrap="nowrap"
      >
        <Grid item style={{ paddingLeft: 10, paddingRight: 10 }}>
          <Typography style={{ color: '#B8B8B8' }} variant="caption">
            Porcentaje de Participación
          </Typography>
          <Typography
            style={{ fontWeight: 900, marginTop: -5 }}
            data-testid="broker-percentage-id"
          >
            {brokers > 0 ? 50 : 100}%
          </Typography>
        </Grid>
        <BrokerPercentageHelp />
      </Grid>
    );
  }
  return '';
}

export const parseToQuotationProfile = (
  profileInput: FormValueTypes,
): QuotationProfile => {
  if (profileInput === '') {
    return {
      id: 4,
      description: '',
    };
  }
  return {
    id: profileInput as number,
    description: '',
  };
};

export const LicenseForm = ({
  licenses,
  isAutoPX,
  additionalBrokers,
  pricingProfiles,
  currentProfileId,
  currentLicenseCode,
  currentAdditionalBrokers,
  subagentsLicenses,
  currentSubagentCode,
  getAutoPxResultAction,
  getDynamicRenderConfigsAction,
  getAdditionalBrokersAction,
  getSubagentsAction,
  updateLicenseFormValuesAction,
  onSubmit,
}: Props): JSX.Element => {
  const classes = useStyles();
  const formClasses = makeFormStyles();
  const [showAutoPxError, setShowAutoPxError] = useState<boolean>(false);
  const [hasAdditionalBrokers, setHasAdditionalBrokers] =
    useState<boolean>(false);
  const [additionalBrokersSelect, dispatch] = useReducer(
    brokerReducer,
    convertToAdditionalBrokerForm(currentAdditionalBrokers),
  );
  // Keep track of the request
  const autopxRequestStatus = useNetworkObserver(GET_AUTO_PX);
  const additionalBrokersStatus = useNetworkObserver(
    REQUEST_ADDITIONAL_BROKERS,
  );
  const requestingConfig = useNetworkObserver(CONFIG_REQUEST);
  const parsedLicensesOptions = licenses.map(
    (license): DisplayItem => ({
      Value: license.code,
      Text: license.name,
    }),
  );
  const parsedProfilesOptions = pricingProfiles.map(
    (profile): DisplayItem => ({
      Value: profile.id.toString(),
      Text: profile.description,
    }),
  );
  const parseAdditionalBrokers = additionalBrokers.map(
    (brokers): DisplayItem => ({
      Value: brokers.code,
      Text: `${brokers.name} - ${brokers.code}`,
    }),
  );
  const parseSubagentsOptions = subagentsLicenses.map(
    (subagent): DisplayItem => ({
      Value: subagent.code,
      Text: `${subagent.name} - ${subagent.license}`,
    }),
  );
  const isRequestingData = useMemo(
    () =>
      autopxRequestStatus === 'requesting' ||
      ['requesting', 'success'].includes(requestingConfig),
    [autopxRequestStatus, requestingConfig],
  );
  const { handleSubmit, getInputProps } = useFormHook<LicenseFormData>(
    {
      license: currentLicenseCode,
      profile: currentProfileId === 0 ? '' : currentProfileId.toString(),
      subagent: currentSubagentCode,
      additionalError: false,
    },
    (): void => {
      const defaultProfile =
        pricingProfiles.length === 1 ? pricingProfiles[0].id.toString() : '4';
      // When profile is empty, means that there is only one profile or is default Vehicle
      getDynamicRenderConfigsAction(
        (getInputProps('profile').value as string) || defaultProfile,
        NetworkClient,
      );
    },
    makeLicenseFormValidation(pricingProfiles.length),
  );
  function handleFormSubmit(e: React.FormEvent): void {
    e.preventDefault();
    dispatch({ type: 'ERROR_HANDLER' });
    getInputProps('additionalError').onChange(
      additionalBrokerHasError(additionalBrokersSelect),
    );
    handleSubmit();
  }
  const licenseInput = getInputProps('license');
  const profileInput = getInputProps('profile');
  const subAgentInput = getInputProps('subagent');
  const isUserRoleASE =
    getLoggedRole() === 'ASE'
      ? true
      : ((licenseInput.value = parsedLicensesOptions[0].Value), false);
  const licenseDefaultText = isUserRoleASE ? '' : parsedLicensesOptions[0].Text;
  // When there is only one profile and license, submits automatically.
  useEffect(() => {
    if (licenses.length === 1 && pricingProfiles.length === 1) {
      const [license] = licenses;
      const [profile] = pricingProfiles;
      licenseInput.onChange(license.code);
      profileInput.onChange(profile.id);
      getDynamicRenderConfigsAction(profile.id.toString(), NetworkClient);
    }
  }, [
    licenses,
    pricingProfiles,
    getDynamicRenderConfigsAction,
    licenseInput,
    profileInput,
  ]);
  useEffect(() => {
    if (requestingConfig === 'success') {
      const licenseValue = licenseInput.value as string;
      const profileValue = profileInput.value as number;
      const usedProfileType = pricingProfiles.find(
        (profile) => profile.id.toString() === profileValue.toString(),
      );
      const additionalBrokersValue = additionalBrokersSelect.map(
        (broker) => broker.value,
      );
      const subAgentsValue = subAgentInput.value as string;
      updateLicenseFormValuesAction(
        licenseValue,
        profileValue,
        additionalBrokersValue,
        usedProfileType as PricingProfileResponse,
        subAgentsValue,
      );
      trackEvent({ eventName: COLLECTIVE_SECTION_STEP, eventProperties: {} });
      onSubmit();
    }
  }, [
    requestingConfig,
    onSubmit,
    getInputProps,
    licenseInput.value,
    profileInput.value,
    additionalBrokersSelect,
    updateLicenseFormValuesAction,
    pricingProfiles,
    subagentsLicenses,
    subAgentInput.value,
  ]);
  /**
   * Checks if autopx applies to this profile and license
   * For ASE only, since AGE will always have AutoPx
   * */
  useEffect(() => {
    if (licenseInput.value !== '' && getLoggedRole() === 'ASE') {
      getAutoPxResultAction(licenseInput.value as string);
    }
  }, [licenseInput.value, getAutoPxResultAction]);
  useEffect(() => {
    // Co-corretage logic
    if (matchesCobrokerageLicense(licenseInput.value as string)) {
      setHasAdditionalBrokers(true);
      getAdditionalBrokersAction(licenseInput.value as string);
    } else {
      setHasAdditionalBrokers(false);
    }
  }, [licenseInput.value, getAdditionalBrokersAction, setHasAdditionalBrokers]);
  /**
   * Checks if autopx applies to this profile and license
   * For ASE only, since AGE will always have AutoPx
   * */
  useEffect(() => {
    if (licenseInput.value !== '') {
      getSubagentsAction(licenseInput.value as string);
    }
  }, [licenseInput.value, getSubagentsAction]);
  useEffect(() => {
    const quotationProfile = parseToQuotationProfile(
      profileInput.value as string,
    );
    const specialPlan = isSpecialPlan(quotationProfile);
    if (specialPlan || isAutoPX) {
      setShowAutoPxError(false);
    } else {
      setShowAutoPxError(true);
    }
  }, [isAutoPX, profileInput.value, setShowAutoPxError]);
  return (
    <form onSubmit={handleFormSubmit} className={classes.content}>
      <Grid container justifyContent="center">
        <Grid item xs={12} md={9}>
          <DefaultCard>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h3">¡Vamos a cotizar!</Typography>
              </Grid>
              <Grid item xs={12}>
                {isUserRoleASE && (
                  <SearchableDropDown
                    inputId="sales-web-dropdown-license"
                    inputName="license"
                    inputLabel="Selecciona la licencia"
                    options={parsedLicensesOptions}
                    optimized
                    defaultValue={licenseInput.value as string}
                    onChange={(newVal): void => {
                      dispatch({ type: 'CLEAR_ALL' });
                      licenseInput.onChange(newVal.Value);
                    }}
                    inputError={licenseInput.error}
                    inputErrorMessage={licenseInput.errorText}
                    isDisabled={!isUserRoleASE}
                  />
                )}
                {!isUserRoleASE && (
                  <TextField
                    inputId="sales-web-dropdown-license"
                    inputName="license"
                    inputLabel="Licencia"
                    defaultValue={licenseDefaultText as string}
                    isDisabled={!isUserRoleASE}
                    customDecoration
                  />
                )}
                {adittionalBrokers(
                  hasAdditionalBrokers,
                  additionalBrokersSelect.length,
                )}
              </Grid>
              {parsedProfilesOptions.length > 1 && (
                <Grid item xs={12} style={{ marginBottom: 10 }}>
                  <SearchableDropDown
                    inputId="sales-web-dropdown-profiles"
                    inputName="profile"
                    inputLabel="Selecciona un plan"
                    options={parsedProfilesOptions}
                    optimized
                    defaultValue={profileInput.value as string}
                    onChange={(newVal): void => {
                      profileInput.onChange(newVal.Value);
                    }}
                    inputError={profileInput.error}
                    inputErrorMessage={profileInput.errorText}
                  />
                </Grid>
              )}
              {parseSubagentsOptions.length > 0 && (
                <Grid item xs={12}>
                  <SearchableDropDown
                    inputId="sales-web-dropdown-subagent"
                    inputName="subagent"
                    inputLabel="Seleccione un Subagente (opcional)"
                    defaultValue={subAgentInput.value as string}
                    options={parseSubagentsOptions}
                    inputError={subAgentInput.error}
                    inputErrorMessage={subAgentInput.errorText}
                    onChange={(newVal): void => {
                      subAgentInput.onChange(newVal.Value);
                    }}
                  />
                </Grid>
              )}
              {/** Should not show error Message if license or profile can potentially have auto px */}
              {showAutoPxError && (
                <Grid item xs={12} data-testid="autopx-error-message">
                  {customTypographyText()}
                </Grid>
              )}
              {hasAdditionalBrokers &&
                additionalBrokersStatus !== 'requesting' && (
                  <Grid item xs={12}>
                    <AdditionalBrokers
                      data-testid="additional-brokers-id"
                      brokersList={parseAdditionalBrokers}
                      brokersValue={additionalBrokersSelect}
                      brokersAction={dispatch}
                    />
                  </Grid>
                )}
              {additionalBrokersStatus === 'requesting' && (
                <Grid item xs={12} style={{ textAlign: 'center' }}>
                  <CircularProgress data-testid="spinner-additional-brokers" />
                </Grid>
              )}
            </Grid>
          </DefaultCard>
        </Grid>
        <Grid item xs={12} md={9} container justifyContent="center">
          <img
            className={classes.svgArt}
            src="/svg/new_quotation_art.svg"
            alt="cotizar"
          />
        </Grid>
        <Grid item xs={12} container justifyContent="center">
          <div className={formClasses.buttonContainer}>
            <DefaultButton
              id="sales-web-button-init"
              data-testid="submit-button"
              type="submit"
              fullWidth
              disabled={isRequestingData}
            >
              {isRequestingData ? (
                <CircularProgress data-testid="spinner-btn" />
              ) : (
                'COMENCEMOS'
              )}
            </DefaultButton>
          </div>
        </Grid>
      </Grid>
    </form>
  );
};
export const mapStateToProps = (
  state: RootState,
): {
  licenses: GeneralCatalogItem[];
  isAutoPX: boolean;
  additionalBrokers: GeneralCatalogItem[];
  currentProfileId: number;
  currentLicenseCode: string;
  currentAdditionalBrokers: string[];
  subagentsLicenses: SubAgentItem[];
  currentSubagentCode: string;
} => ({
  licenses: state.profile.licenses,
  isAutoPX: state.quotation.currentQuotation.isAutoPx,
  additionalBrokers: state.catalog.additionalBrokers,
  currentProfileId: state.quotation.usedProfile,
  currentLicenseCode: state.quotation.usedLicense,
  currentAdditionalBrokers: state.quotation.usedAdditionalBroker,
  subagentsLicenses: state.quotation.subAgentList,
  currentSubagentCode: state.quotation.usedsubAgentLicense,
});
const reduxActions = {
  getAutoPxResultAction: getAutoPxResult,
  getDynamicRenderConfigsAction: getDynamicRenderConfigs,
  updateLicenseFormValuesAction: updateLicenseFormValues,
  getAdditionalBrokersAction: getAdditionalBrokers,
  getSubagentsAction: getSubagents,
};
const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  onSubmit: () => void;
  pricingProfiles: PricingProfileResponse[];
}

export default connector(LicenseForm);
